import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLastArticlesByParent } from "@administration/hooks/ArticlesHook";
import { useSites } from "@shared/hooks/SiteHook";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useCallback, useEffect, useMemo, useState } from "react";
import ArticleCard, { ArticleCardSkeleton } from "./ArticleCard";
const ARTICLES_DISPLAY_BATCH_SIZE = 6;
const BlogTemplate = (props) => {
    const { page } = props;
    const { currentSite } = useSites();
    const theme = useTheme();
    const isMobile = !useMediaQuery(theme.breakpoints.up("md"));
    const [selectedTag, setSelectedTag] = useState("");
    const [pagination, setPagination] = useState(1);
    const [displayedArticles, setDisplayedArticles] = useState([]);
    const { contents = {}, page: pageInfo } = page;
    const { dynamicPart } = contents;
    const filters = selectedTag
        ? { tags: { operator: "like", values: [selectedTag] } }
        : {};
    const { data, isFetching } = useLastArticlesByParent(pageInfo?.id, currentSite?.id, {
        take: ARTICLES_DISPLAY_BATCH_SIZE,
        skip: (pagination - 1) * ARTICLES_DISPLAY_BATCH_SIZE,
    }, filters);
    const { data: articles = [], total = 0 } = data || {};
    // Update the displayed articles whenever the articles, fetching status, or pagination changes
    useEffect(() => {
        if (isFetching) {
            return;
        }
        setDisplayedArticles((currentArticles) => {
            if (pagination === 1) {
                return articles;
            }
            const displayedIds = new Set(currentArticles.map((article) => article.id));
            return [
                ...currentArticles,
                ...articles.filter((article) => !displayedIds.has(article.id)),
            ];
        });
    }, [articles, isFetching, pagination]);
    const skeletonCount = useMemo(() => {
        return isFetching
            ? Math.max(0, Math.min(ARTICLES_DISPLAY_BATCH_SIZE, total - displayedArticles.length))
            : 0;
    }, [isFetching, total, displayedArticles.length]);
    const handleLoadMore = useCallback(() => {
        if (displayedArticles.length < total) {
            setPagination((currentPage) => currentPage + 1);
        }
    }, [displayedArticles.length, total]);
    return (_jsx(Container, { sx: {
            maxWidth: "1208px !important",
            py: { xs: "40px", md: "64px" },
            "& .cms-core-block": { p: 0 },
        }, children: _jsxs(Stack, { spacing: { xs: 5, md: 7 }, children: [displayedArticles.length === 0 && !isFetching ? (_jsx(Typography, { variant: "subtitle1", component: "p", textAlign: "center", children: "Aucun article" })) : (_jsxs(Grid, { container: true, spacing: 3, "aria-busy": isFetching, children: [displayedArticles.map((article) => (_jsx(ArticleCard, { page: article, direction: isMobile ? "horizontal" : "vertical" }, article.id))), Array.from({ length: skeletonCount }, (_, index) => (_jsx(ArticleCardSkeleton, { direction: isMobile ? "horizontal" : "vertical" }, `skeleton-${index}`)))] })), displayedArticles.length > 0 && (_jsxs(Stack, { direction: "row", spacing: 4, sx: {
                        justifyContent: "center",
                        alignItems: "center",
                    }, children: [_jsxs(Typography, { component: "p", variant: "subtitle2", textAlign: "center", children: [displayedArticles.length, " article", displayedArticles.length > 1 ? "s" : "", " / ", total] }), (displayedArticles.length < total || isFetching) && (_jsx(Button, { variant: "contained", color: "secondary", onClick: handleLoadMore, loading: isFetching, children: "Afficher plus" }))] })), dynamicPart] }) }));
};
export default BlogTemplate;
